import {PIXI} from '@colonist/pixi'
import {uiGameManager} from '../../EntryShared.ts'
import {TextView} from '../../ui_canvas_engine/TextView.ts'
import {GameColor} from '../../GameColor.ts'
import {UIGameToolTipBgView} from './UIGameToolTipBgView.ts'
import {UIGameToolTipPointerView} from './UIGameToolTipPointerView.ts'
import {type TranslationObject} from '@colonist/i18n'
import {clientI18n} from '../../ClientI18n.ts'
import {type UIGameCanvasController} from '../UIGameCanvasController.ts'
import {SectionBgView} from '../../ui_canvas_engine/SectionBgView.ts'
import {isOverlapping} from '../../ui_utils/PixiUtils.ts'
import {type CardEnum} from '@colonist/game-states'

export abstract class UIGameToolTipView extends PIXI.Container {
    protected readonly uiGameManager = uiGameManager

    readonly viewToPlaceNear?: PIXI.Container | PIXI.Sprite
    private readonly isProgressCardTooltip: boolean = false

    private readonly backgroundColor: GameColor.Color = GameColor.getColorHexValue(GameColor.Color.Gray0)

    private readonly title: TranslationObject
    private readonly bodyParagraphs: TranslationObject[] = []
    protected titleTextView: TextView
    private readonly bodyTextsView: TextView[] = []
    private readonly hasBodyPart: boolean = false
    protected readonly textColor = GameColor.getColor(GameColor.Color.GrayScale25)
    private toolTipBgView: UIGameToolTipBgView

    protected titleFont
    protected bodyFont

    protected itemSize = uiGameManager.canvasController.itemSize
    protected maxTipWidth: number
    protected horizontalPadding: number
    protected titleTopPadding: number
    protected titleBottomPadding: number
    protected bodyTopPadding: number
    protected bodyLineHeight: number

    protected tooltipH: number
    protected tooltipW: number
    protected titleH: number

    private readonly cornerRadius: number = 8

    constructor(viewToPlaceNear: PIXI.Container | PIXI.Sprite, title: TranslationObject, bodyParagraphs?: TranslationObject[], developmentCards?: CardEnum[]) {
        super()
        this.viewToPlaceNear = viewToPlaceNear
        if(developmentCards) this.isProgressCardTooltip = true
        this.title = title
        for(const paragraph of bodyParagraphs ?? []) {
            if(paragraph.key === 'strings:colonistCommon.utils.emptyString') continue
            this.bodyParagraphs.push(paragraph)
        }
        this.hasBodyPart = this.bodyParagraphs.length > 0 || this.isProgressCardTooltip

        this.setConstants()
        this.increaseMaxWidthIfNeeded()
        this.createStyledTexts()
        this.setDimensions()
        this.createBg()
        this.repositionTexts()
        uiGameManager.canvasController.app.stage.addChild(this)
        this.setCoordinates()
        this.createPointer(viewToPlaceNear)
    }

    protected abstract setConstants()

    private increaseMaxWidthIfNeeded() {
        if(this.isProgressCardTooltip) return
        if(!this.hasBodyPart) {
            this.maxTipWidth *= 2
            return
        }

        const words = this.bodyParagraphs.map((text) => clientI18n.t(text.key, text.options)).join(' ')
        const wordsArray = words.split(' ')
        const wordCount = wordsArray.length
        if(wordCount > 40) {
            this.maxTipWidth *= 2
        } else if(wordCount > 25) {
            this.maxTipWidth *= 1.5
        } else if(wordCount > 20) {
            this.maxTipWidth *= 1.25
        }
    }

    private createStyledTexts() {
        this.titleTextView = this.getText(this.title, this.titleFont, this.horizontalPadding)
        this.titleTextView.makeBold()

        for(const paragraph of this.bodyParagraphs) {
            const bodyTextView = this.getText(paragraph, this.bodyFont, this.horizontalPadding)
            bodyTextView.changeStyle({lineHeight: this.bodyLineHeight})
            bodyTextView.removeBold()
            this.bodyTextsView.push(bodyTextView)
        }
    }

    private getText(paragraph: TranslationObject, font: number, x: number): TextView {
        const availableTextWidth = this.maxTipWidth - this.horizontalPadding * 2
        const textView = new TextView(paragraph, x, this.titleTopPadding, availableTextWidth, 0, this, font, this.textColor, false)
        return textView
    }

    private setDimensions() {
        this.setRealWidth()
        this.setHeights()
    }

    protected setRealWidth() {
        let maxTextWidth = this.titleTextView.width
        for(const bodyText of this.bodyTextsView) {
            if(maxTextWidth < bodyText.width) maxTextWidth = bodyText.width
        }
        this.tooltipW = maxTextWidth + this.horizontalPadding * 2
    }

    protected setHeights() {
        this.titleH = this.getTitleHeight()
        this.adjustTitleHeightForNoBody()
        const bodyH = this.getBodyHeight()
        this.tooltipH = this.titleH + bodyH
    }

    private getTitleHeight(): number {
        if(!this.hasTitle()) return 0
        return this.titleTextView.height + this.titleTopPadding + this.titleBottomPadding
    }

    private adjustTitleHeightForNoBody() {
        if(!this.hasBodyPart) this.titleH *= 1.2
    }

    private getBodyHeight(): number {
        if(!this.hasBodyPart) return 0
        return this.getBodyTextViewHeights() + this.getBodyPaddings()
    }

    private getBodyTextViewHeights(): number {
        let height = 0
        if(!this.hasTitle()) height += this.titleBottomPadding
        for(const bodyText of this.bodyTextsView) {
            height += bodyText.height
        }
        return height
    }

    private getBodyPaddings(): number {
        const paddingBetweenBodyTexts = (this.bodyTextsView.length - 1) * this.bodyTopPadding
        return this.bodyTopPadding + paddingBetweenBodyTexts
    }

    private createBg() {
        this.toolTipBgView = new UIGameToolTipBgView(this, this.tooltipW, this.tooltipH, this.cornerRadius, this.backgroundColor)
    }

    private createPointer(viewToPlaceNear: PIXI.Container | PIXI.Sprite) {
        const bounds = viewToPlaceNear.getBounds()
        const x = bounds.x - this.x + bounds.width / 2
        new UIGameToolTipPointerView(this, x, viewToPlaceNear, this.cornerRadius, this.backgroundColor)
    }

    protected repositionTexts() {
        if(!this.hasBodyPart) {
            this.titleTextView.centerYInParent()
            return
        }
        this.titleTextView.changeStyle({align: 'left'})

        const initialBodyYWithTitle = this.titleH + this.bodyTopPadding * 0.1
        const initialBodyY = this.hasTitle() ? initialBodyYWithTitle : this.bodyTopPadding
        for(const [index, bodyText] of this.bodyTextsView.entries()) {
            const previousBodyTextsHeight = this.getPreviousBodyTextsHeight(index)
            bodyText.changeStyle({align: 'left'})
            bodyText.y = initialBodyY + previousBodyTextsHeight
        }
    }

    private getPreviousBodyTextsHeight(index: number): number {
        return this.bodyTextsView.slice(0, index).reduce((height: number, text: TextView) => height + text.height + this.bodyTopPadding, 0)
    }

    private hasTitle(): boolean {
        return this.title.key !== 'strings:colonistCommon.utils.emptyString'
    }

    private checkOverlapsWithSectionBgViews(): boolean {
        const stage = uiGameManager.canvasController.app.stage
        const toolTipBgBounds = this.toolTipBgView.getBounds()

        for(const child of stage.children) {
            if(child instanceof SectionBgView == false) continue
            if(child.visible == false) continue

            const childBounds = child.getBounds()
            if(isOverlapping(childBounds, toolTipBgBounds)) return true
        }

        return false
    }

    private checkOverlapsWithChatLog(canvas: UIGameCanvasController): boolean {
        const isOverlappingLogChatX = this.x + this.tooltipW > canvas.appW - canvas.chatWidth
        const isOverlappingLogChatY = this.y < canvas.appH - canvas.itemSize * 5.5
        return isOverlappingLogChatX && isOverlappingLogChatY
    }

    private checkOverlapsWithMenuButtons(canvas: UIGameCanvasController): boolean {
        const menuButtonsWidth = canvas.itemSize * 0.8
        const menuButtonsHeight = canvas.itemSize * 2
        const isOverlappingMenuButtonsX = this.x < menuButtonsWidth
        const isOverlappingMenuButtonsY = this.y < menuButtonsHeight
        return isOverlappingMenuButtonsX && isOverlappingMenuButtonsY
    }

    private setCoordinateX(canvas: UIGameCanvasController) {
        if(!this.viewToPlaceNear) return

        const globalBounds = this.viewToPlaceNear.getBounds()
        this.x = globalBounds.x + globalBounds.width / 2 - this.tooltipW / 2

        const isOverlappingRight = this.x + this.tooltipW > canvas.appW
        const isOverlappingLeft = this.x < 0

        if(isOverlappingRight) this.x = canvas.appW - this.tooltipW
        if(isOverlappingLeft) this.x = 0
    }

    private setCoordinateY(canvas: UIGameCanvasController) {
        if(!this.viewToPlaceNear) return

        const spacingForPointer = this.itemSize / 4
        const belowY = this.viewToPlaceNear.getBounds().bottom + spacingForPointer
        const aboveY = this.viewToPlaceNear.getBounds().top - this.tooltipH - spacingForPointer

        this.y = belowY
        const isOverlappingBelow = this.checkOverlapsWithSectionBgViews()
        this.y = aboveY
        const isOverlappingAbove = this.checkOverlapsWithSectionBgViews()

        if(isOverlappingBelow && !isOverlappingAbove) this.y = aboveY
        else this.y = belowY

        const isOverlappingBottom = this.y + this.tooltipH > canvas.appH
        const isOverlappingTop = this.y < 0

        if(isOverlappingBottom) this.y = aboveY
        if(isOverlappingTop) this.y = belowY
    }

    private setCoordinates() {
        if(!this.viewToPlaceNear) return

        const canvas = uiGameManager.canvasController

        this.setCoordinateX(canvas)
        this.setCoordinateY(canvas)

        const belowY = this.viewToPlaceNear.getBounds().bottom + this.itemSize / 4
        if(this.checkOverlapsWithChatLog(canvas)) this.y = belowY
        if(this.checkOverlapsWithChatLog(canvas)) this.x = canvas.appW - this.tooltipW - canvas.chatWidth
        if(this.checkOverlapsWithMenuButtons(canvas)) this.y = belowY
        if(this.checkOverlapsWithMenuButtons(canvas)) this.x = canvas.itemSize * 0.8
    }
}
